import { featuredGuestJourneyService } from 'src/service/featured-guest-journey-service'

/** The standing pitch, shown to a visitor who has not just finished something. */
const DEFAULT_LEAD_IN = 'Get a flavor of our workouts.'

/**
 * The one line of copy that sits directly above the public workouts, in both placements.
 *
 * A guest reaches the player from this page and comes back to it by the player's own "Back to
 * sign in" link. Returning them to the identical pitch is the page forgetting what they just
 * did, at the moment they are most disposed to register — so after a recent completion the
 * line acknowledges it instead.
 *
 * **Why this line and not the hero panel's `<h2>`.** The `THE HOME OF STRENGTH TRAINING`
 * heading only exists in `featured-workouts-hero-panel.tsx`, which renders at `lg` and up;
 * below that a rail renders instead and there is no heading at all. This lead-in is the one
 * element present in *both* placements, so swapping it reaches every viewport. (Mobile swaps
 * its main headline, because there the headline is always on screen.)
 *
 * **Why the copy is forward-looking.** Nothing the visitor just did was saved: the guest player
 * writes no completion, metrics or progress, and nothing back-fills them on sign-up. "Save your
 * workout" would be contradicted by the first empty history screen. Making that promise honest
 * means writing the guest's completion on first sign-in, which is tracked separately.
 *
 * Read at render rather than held in state: `justCompleted()` is a synchronous localStorage
 * read, both placements mount fresh on arrival, and there is nothing to subscribe to.
 *
 * ⚠️ **The wording is shared with mobile verbatim** — see `auth-screen.tsx` there, which lays
 * the same sentences out as a headline plus a body because it has the room. Keep them in step:
 * the apps cannot import from each other, so this is a convention, not a constraint the
 * compiler enforces.
 *
 * ⚠️ **Its LENGTH is load-bearing on the phone rail.** At 390×844 the rail's lead-in sits above
 * the cards, and `auth/featured-workouts.public.spec.ts` asserts that a card *and* the email
 * field both stay in the viewport. This string wraps to about three lines there. If it grows,
 * re-run that spec rather than assuming — the layout has little slack, and the failure mode is
 * the sign-in form sliding out of the first screen.
 */
export const featuredWorkoutsLeadIn = (): string =>
  featuredGuestJourneyService.justCompleted()
    ? "Nice work. That one's on us. Create an account and your next workout counts — your history, your progress, your streak."
    : DEFAULT_LEAD_IN
